import {
  ShopAttributeDataType,
  ShopProductAttributeSource,
} from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Input, cn } from '@utima/ui';
import { Check, Pencil, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import {
  memo,
  useCallback,
  useMemo,
  useState,
  type KeyboardEvent,
} from 'react';

import { Card } from '@/components/card/Card';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { useEnumTranslations } from '@/hooks';
import type { RouterOutputs } from '@/services/trpc';

import { AddAttributeValueDialog } from './AddAttributeValueDialog';
import { parseComparableNumber } from '../attributeEditing';
import { useShopProductAttributeValueMutation } from '../useShopProductAttributeValueMutation';

type ProductAttributeValue = NonNullable<
  RouterOutputs['shopProducts']['findOne']['attributes']
>[number];

const SOURCE_BADGE_VARIANT: Record<
  ShopProductAttributeSource,
  'info' | 'secondary' | 'success'
> = {
  [ShopProductAttributeSource.Feed]: 'info',
  [ShopProductAttributeSource.Model]: 'secondary',
  [ShopProductAttributeSource.Manual]: 'success',
};

export type ProductAttributesCardProps = {
  productId: string;
  attributes: ProductAttributeValue[];
  chatId?: string;
  feedId?: string;
};

/**
 * The per-product half of the attribute lifecycle: add a value, correct one,
 * remove it, put it back.
 *
 * Removed values stay on screen, struck through and marked as removed, rather
 * than disappearing — the inactive row *is* the record of the decision, and
 * someone who removed the wrong one needs to find it again to put it back.
 *
 * The wording throughout keeps this scope visible: everything here says "this
 * product". Switching an attribute off for the whole feed is a different
 * action on a different screen, and the note at the top of the card points
 * there rather than offering it inline. Adding a value is the one action that
 * can reach the feed's vocabulary — by creating an attribute that does not
 * exist yet — and its dialog names that scope where it happens.
 */
export const ProductAttributesCard = memo(function ProductAttributesCard({
  productId,
  attributes,
  chatId,
  feedId,
}: ProductAttributesCardProps) {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();
  const { updateValue, correctValue, removeValue, restoreValue } =
    useShopProductAttributeValueMutation();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [draftValue, setDraftValue] = useState('');

  /**
   * Held as the raw string the admin typed rather than a number, so an empty
   * field stays empty instead of collapsing to 0. Empty is a real answer: it
   * means this row is not comparable to other products.
   */
  const [draftNumericValue, setDraftNumericValue] = useState('');

  // Scoped to the row actually being written — `isPending` alone would spin
  // every row's buttons on any single save.
  const pendingId = updateValue.isPending
    ? updateValue.variables?.id
    : undefined;

  const startEditing = useCallback(
    ({
      id,
      value,
      numericValue,
    }: {
      id: string;
      value: string;
      numericValue: number | null | undefined;
    }) => {
      setEditingId(id);
      setDraftValue(value);
      setDraftNumericValue(
        numericValue === null || numericValue === undefined
          ? ''
          : String(numericValue),
      );
    },
    [],
  );

  const stopEditing = useCallback(() => {
    setEditingId(null);
    setDraftValue('');
    setDraftNumericValue('');
  }, []);

  const handleSave = useCallback(
    async ({ id, isNumeric }: { id: string; isNumeric: boolean }) => {
      const value = draftValue.trim();

      if (!value) {
        return;
      }

      const comparable = parseComparableNumber(draftNumericValue);

      /**
       * A field left empty clears the number on purpose. Anything that does
       * not parse is a typo rather than an intention, so the save is held
       * back instead of silently storing `null` and dropping the row out of
       * every range filter.
       */
      if (isNumeric && !comparable.isValid) {
        return;
      }

      await correctValue({
        id,
        value,
        // Omitted entirely for a non-numeric attribute: the form shows no
        // number for those, so it has no business clearing one.
        ...(isNumeric ? { numericValue: comparable.value } : {}),
      });
      stopEditing();
    },
    [correctValue, draftValue, draftNumericValue, stopEditing],
  );

  const takenAttributeIds = useMemo(
    () => attributes.map(({ attribute }) => attribute.id),
    [attributes],
  );

  return (
    <>
      <Card title={t`Attributes`}>
        <p className='text-xs text-muted-fg'>
          {chatId && feedId ? (
            <Trans>
              Removing a value affects this product only — the attribute stays
              available everywhere else. To switch it off across the whole feed,
              open{' '}
              <Link
                to='/chats/$chatId/feeds/$feedId/attributes'
                params={{ chatId, feedId }}
                className='text-primary underline'
              >
                Feed attributes
              </Link>
              .
            </Trans>
          ) : (
            <Trans>
              Removing a value affects this product only — the attribute stays
              available everywhere else. To switch it off across the whole feed,
              open the attributes screen for this feed.
            </Trans>
          )}
        </p>

        {feedId && (
          <Button
            data-cta='admin-product-attribute-add-button'
            variant='ghost'
            outline
            size='sm'
            className='w-fit'
            onClick={() => setIsAdding(true)}
          >
            <Plus className='size-4' />
            <Trans>Add a value</Trans>
          </Button>
        )}

        {attributes.length === 0 ? (
          <p className='text-sm text-muted-fg'>
            <Trans>
              This product has no attributes yet, from the feed or from
              enrichment.
            </Trans>
          </p>
        ) : (
          <ul className='flex flex-col gap-2'>
            {attributes.map(
              ({ id, value, numericValue, active, source, attribute }) => {
                const isEditing = editingId === id;
                const isNumeric =
                  attribute.dataType === ShopAttributeDataType.Number;
                const unitLabel = attribute.baseUnit ?? attribute.unit ?? '';
                const isPending = pendingId === id;

                /**
                 * `value` carries its own unit already ("500 ml"), so this is the
                 * separate number range filters actually compare, in the
                 * attribute's base unit. Empty when the row is not comparable.
                 */
                const comparableLabel =
                  numericValue === null || numericValue === undefined
                    ? ''
                    : [numericValue, unitLabel].filter(Boolean).join(' ');

                const handleRowKeyDown = (
                  event: KeyboardEvent<HTMLInputElement>,
                ) => {
                  if (event.key === 'Enter') {
                    void handleSave({ id, isNumeric });
                  }

                  if (event.key === 'Escape') {
                    stopEditing();
                  }
                };

                return (
                  <li
                    key={id}
                    className={cn(
                      'flex flex-col gap-2 rounded-md border border-border p-3 md:flex-row md:items-start md:justify-between',
                      !active && 'border-dashed bg-muted/20',
                    )}
                  >
                    <div className='flex min-w-0 flex-col gap-1.5'>
                      <div className='flex flex-wrap items-center gap-1.5'>
                        <span className='text-sm font-bold'>
                          {attribute.displayName ?? attribute.name}
                        </span>
                        <Badge size='xs' outline variant='muted'>
                          {
                            enumTranslations.shopAttributeDataType[
                              attribute.dataType
                            ]
                          }
                        </Badge>
                        <Badge
                          size='xs'
                          outline
                          variant={SOURCE_BADGE_VARIANT[source]}
                        >
                          {enumTranslations.shopProductAttributeSource[source]}
                        </Badge>
                        {attribute.unit && (
                          <Badge size='xs' outline variant='ghost'>
                            {attribute.unit}
                          </Badge>
                        )}
                        {attribute.baseUnit &&
                          attribute.baseUnit !== attribute.unit && (
                            <Badge size='xs' outline variant='ghost'>
                              {t`Compared in ${attribute.baseUnit}`}
                            </Badge>
                          )}
                        {!active && (
                          <Badge size='xs' variant='danger'>
                            <Trans>Removed from this product</Trans>
                          </Badge>
                        )}
                        {!attribute.active && (
                          <Badge size='xs' variant='warning'>
                            <Trans>Switched off for the whole feed</Trans>
                          </Badge>
                        )}
                      </div>

                      {isEditing ? (
                        <div className='flex flex-col gap-1.5'>
                          <div className='flex flex-wrap items-end gap-3'>
                            <div className='flex min-w-48 flex-1 flex-col gap-1'>
                              <span className='text-xs text-muted-fg'>
                                <Trans>Shown to shoppers</Trans>
                              </span>
                              <Input
                                size='sm'
                                autoFocus
                                value={draftValue}
                                disabled={updateValue.isPending}
                                onChange={event =>
                                  setDraftValue(event.target.value)
                                }
                                onKeyDown={handleRowKeyDown}
                                aria-label={t`Attribute value`}
                              />
                            </div>

                            {isNumeric && (
                              <div className='flex w-40 flex-col gap-1'>
                                <span className='text-xs text-muted-fg'>
                                  <Trans>Compared as</Trans>
                                </span>
                                <div className='flex items-center gap-1.5'>
                                  <Input
                                    size='sm'
                                    inputMode='decimal'
                                    value={draftNumericValue}
                                    disabled={updateValue.isPending}
                                    onChange={event =>
                                      setDraftNumericValue(event.target.value)
                                    }
                                    onKeyDown={handleRowKeyDown}
                                    aria-label={t`Comparable number`}
                                  />
                                  {unitLabel && (
                                    <span className='shrink-0 text-sm font-medium text-muted-fg'>
                                      ({unitLabel})
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {isNumeric && (
                            <span className='text-xs text-muted-fg'>
                              <Trans>
                                The number is what range filters compare. Leave
                                it empty if this product should not be compared
                                on this attribute.
                              </Trans>
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className='flex flex-col gap-0.5'>
                          <span
                            className={cn(
                              'break-words text-sm',
                              !active &&
                                'text-muted-fg line-through decoration-danger',
                            )}
                          >
                            {value}
                          </span>
                          {isNumeric && (
                            <span className='text-xs text-muted-fg'>
                              {comparableLabel
                                ? t`Compared as ${comparableLabel}`
                                : t`Not comparable to other products`}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className='flex shrink-0 items-center gap-1'>
                      {isEditing ? (
                        <>
                          <SimpleTooltip title={t`Save`}>
                            <Button
                              variant='ghost'
                              outline
                              size='icon-xs'
                              className='text-success'
                              loading={isPending}
                              onClick={() => handleSave({ id, isNumeric })}
                              aria-label={t`Save`}
                            >
                              <Check className='size-4' />
                            </Button>
                          </SimpleTooltip>
                          <SimpleTooltip title={t`Cancel`}>
                            <Button
                              variant='ghost'
                              outline
                              size='icon-xs'
                              disabled={updateValue.isPending}
                              onClick={stopEditing}
                              aria-label={t`Cancel`}
                            >
                              <X className='size-4' />
                            </Button>
                          </SimpleTooltip>
                        </>
                      ) : (
                        <>
                          <SimpleTooltip title={t`Edit value for this product`}>
                            <Button
                              variant='ghost'
                              outline
                              size='icon-xs'
                              disabled={updateValue.isPending}
                              onClick={() =>
                                startEditing({ id, value, numericValue })
                              }
                              aria-label={t`Edit value for this product`}
                            >
                              <Pencil className='size-4' />
                            </Button>
                          </SimpleTooltip>
                          {active ? (
                            <SimpleTooltip title={t`Remove from this product`}>
                              <Button
                                variant='ghost'
                                outline
                                size='icon-xs'
                                className='text-danger'
                                loading={isPending}
                                onClick={() => removeValue({ id })}
                                aria-label={t`Remove from this product`}
                              >
                                <Trash2 className='size-4' />
                              </Button>
                            </SimpleTooltip>
                          ) : (
                            <SimpleTooltip title={t`Restore to this product`}>
                              <Button
                                variant='ghost'
                                outline
                                size='icon-xs'
                                className='text-success'
                                loading={isPending}
                                onClick={() => restoreValue({ id })}
                                aria-label={t`Restore to this product`}
                              >
                                <RotateCcw className='size-4' />
                              </Button>
                            </SimpleTooltip>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                );
              },
            )}
          </ul>
        )}
      </Card>

      {feedId && (
        <AddAttributeValueDialog
          open={isAdding}
          productId={productId}
          feedId={feedId}
          takenAttributeIds={takenAttributeIds}
          onClose={() => setIsAdding(false)}
        />
      )}
    </>
  );
});
